<script setup>
import { computed } from 'vue';
import { CheckCircleFilled, LockOutlined } from '@ant-design/icons-vue';
import { buildFinanceStatusVisual } from '@/utils/financeHealthCheck';

const props = defineProps({
    totals: {
        type: Object,
        required: true
    },
    steps: {
        type: Array,
        default: () => []
    },
    currentStep: {
        type: Number,
        default: 0
    }
});

const emit = defineEmits(['step-change']);

const status = computed(() => buildFinanceStatusVisual(props.totals));

const onStepClick = (index, step) => {
    if (step?.key === 'conclusion') {
        if (!step.editable && !step.done) return;
        emit('step-change', index);
        return;
    }
    if (!step?.data?.id) return;
    emit('step-change', index);
};

const stepStateLabel = (step, index) => {
    if (index === props.currentStep) return '目前';
    if (step.locked) return '鎖定';
    if (step.done && step.editable) return '可編輯';
    if (step.done) return '完成';
    if (step.editable) return '進行中';
    return '';
};
</script>

<template>
    <section class="finance-status-banner admin-panel" :class="`is-${status.tone}`">
        <div class="finance-status-banner__status">
            <span class="finance-status-banner__mark" aria-hidden="true">
                {{ status.symbol }}
            </span>
            <div class="finance-status-banner__copy">
                <div class="finance-status-banner__title">{{ status.label }}</div>
                <p class="finance-status-banner__hint">{{ status.hint }}</p>
            </div>
        </div>

        <div v-if="steps.length" class="finance-status-banner__steps">
            <button
                v-for="(step, index) in steps"
                :key="step.key"
                type="button"
                class="finance-status-banner__step"
                :class="{
                    'is-current': index === currentStep,
                    'is-disabled':
                        step.key === 'conclusion'
                            ? !step.editable && !step.done
                            : !step.data?.id,
                    'is-locked': step.locked,
                    'is-done': step.done && index !== currentStep
                }"
                @click="onStepClick(index, step)"
            >
                <span class="finance-status-banner__step-icon" aria-hidden="true">
                    <LockOutlined v-if="step.locked" />
                    <CheckCircleFilled v-else-if="step.done" />
                </span>
                <span class="finance-status-banner__step-title">{{ step.title }}</span>
                <span
                    v-if="stepStateLabel(step, index)"
                    class="finance-status-banner__step-badge"
                    :class="{
                        'is-current': index === currentStep,
                        'is-locked': step.locked && index !== currentStep,
                        'is-editable': step.done && step.editable && index !== currentStep
                    }"
                >
                    {{ stepStateLabel(step, index) }}
                </span>
            </button>
        </div>
    </section>
</template>

<style scoped>
.finance-status-banner {
    display: grid;
    grid-template-columns: minmax(180px, 240px) minmax(0, 1fr);
    gap: 12px 16px;
    align-items: center;
    margin-bottom: 10px;
    padding: 10px 12px;
    border-radius: 12px;
    border: 1px solid transparent;
}

.finance-status-banner__status {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.finance-status-banner__mark {
    width: 40px;
    height: 40px;
    border-radius: 11px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    line-height: 1;
    flex-shrink: 0;
    border: 1px solid transparent;
}

.finance-status-banner__copy {
    min-width: 0;
}

.finance-status-banner__title {
    font-size: 16px;
    font-weight: 800;
    line-height: 1.2;
    color: #0f172a;
    letter-spacing: 0.01em;
}

.finance-status-banner__hint {
    margin: 3px 0 0;
    font-size: 12px;
    line-height: 1.4;
    color: #64748b;
}

.finance-status-banner__steps {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
}

.finance-status-banner__step {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-width: 0;
    min-height: 56px;
    padding: 10px 8px;
    border: 1px solid #e8e2d6;
    border-radius: 10px;
    background: #fff;
    text-align: center;
    cursor: pointer;
    transition:
        border-color 0.15s ease,
        background 0.15s ease,
        box-shadow 0.15s ease;
}

.finance-status-banner__step:not(.is-disabled):hover {
    border-color: #c4a35a;
    background: #faf7f1;
}

.finance-status-banner__step.is-disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.finance-status-banner__step.is-done {
    border-color: #d6c7a3;
    background: #faf7f1;
}

.finance-status-banner__step.is-locked:not(.is-current) {
    background: #f8fafc;
    border-color: #e2e8f0;
}

.finance-status-banner__step.is-current {
    border-color: #8b6914;
    background: #f6f1e6;
    box-shadow: 0 0 0 1px rgba(139, 105, 20, 0.12);
}

.finance-status-banner__step-icon {
    position: absolute;
    top: 6px;
    right: 8px;
    font-size: 12px;
    line-height: 1;
    color: #94a3b8;
}

.finance-status-banner__step.is-done .finance-status-banner__step-icon {
    color: #8b6914;
}

.finance-status-banner__step.is-locked .finance-status-banner__step-icon {
    color: #64748b;
}

.finance-status-banner__step-title {
    font-size: 14px;
    font-weight: 800;
    letter-spacing: 0.02em;
    line-height: 1.2;
    color: #0f172a;
}

.finance-status-banner__step-badge {
    padding: 0 7px;
    border-radius: 999px;
    background: #e8e2d6;
    color: #5c5346;
    font-size: 10px;
    font-weight: 700;
    line-height: 16px;
}

.finance-status-banner__step-badge.is-current {
    background: #8b6914;
    color: #fffdf8;
}

.finance-status-banner__step-badge.is-locked {
    background: #e2e8f0;
    color: #475569;
}

.finance-status-banner__step-badge.is-editable {
    background: #ede4d0;
    color: #6b5428;
}

.finance-status-banner.is-crisis {
    background: linear-gradient(135deg, #fff7ed 0%, #ffffff 72%);
    border-color: #fdba74;
}
.finance-status-banner.is-crisis .finance-status-banner__mark {
    background: #ffedd5;
    border-color: #fdba74;
}

.finance-status-banner.is-warning {
    background: linear-gradient(135deg, #fffbeb 0%, #ffffff 72%);
    border-color: #fcd34d;
}
.finance-status-banner.is-warning .finance-status-banner__mark {
    background: #fef3c7;
    border-color: #fcd34d;
}

.finance-status-banner.is-climbing {
    background: linear-gradient(135deg, #f8f4eb 0%, #ffffff 72%);
    border-color: #d4b56a;
}
.finance-status-banner.is-climbing .finance-status-banner__mark {
    background: #ede4d0;
    border-color: #c4a35a;
}

.finance-status-banner.is-shore {
    background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 72%);
    border-color: #86efac;
}
.finance-status-banner.is-shore .finance-status-banner__mark {
    background: #dcfce7;
    border-color: #86efac;
}

@media (max-width: 900px) {
    .finance-status-banner {
        grid-template-columns: 1fr;
    }

    .finance-status-banner__steps {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
